import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useContentMetafieldData } from '@jbhi-fi/components/src/hooks/useContentMetafieldData/useContentMetafieldData';
import { UseContentProps } from '@jbhi-fi/components/src/types';
import { SubCategoryProps } from '../../views/SubCategory/SubCategory';
import { contentfulApiSubCategoryPageToSubCategoryProps } from '../../transformers/contentfulApiSubCategoryPageToSubCategoryProps';
import { CardProvider } from '@jbhi-fi/components/src/hooks/useCards';
import { ContentfulSubCategoryPage, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { useContentfulEntries } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { getDateOnly } from '@jbhi-fi/utils/src/date';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export const SubCategoryPropsContext = createContext<UseContentProps<SubCategoryProps> | null>(null);

export const SubCategoryPropsProvider = ({ children }: { children: ReactNode }) => {
  const metafieldData = useContentMetafieldData<ContentfulSubCategoryPage>();
  const isDesktop = useBreakpoint('md', 'up');

  const { entries, isLoading, error } = useContentfulEntries<ContentfulSubCategoryPage>({
    filter: {
      content_type: EntryType.subCategory,
      'sys.id': metafieldData.sys.id,
    },
    autoRefresh: window.Jbhifi.isContentPreview,
  });

  const data = entries?.length && entries[0];
  const [subCategoryProps, setSubCategoryProps] = useState<SubCategoryProps>(
    contentfulApiSubCategoryPageToSubCategoryProps(metafieldData)
  );

  useEffect(() => {
    setSubCategoryProps(contentfulApiSubCategoryPageToSubCategoryProps(data || metafieldData));
  }, [data]);

  return !subCategoryProps?.cardsFilter ? null : (
    <SubCategoryPropsContext.Provider value={{ data: subCategoryProps, isLoading, error }}>
      <CardProvider
        filters={[
          {
            ...subCategoryProps?.cardsFilter,
            content_type: EntryType.article,
            order: '-fields.publishedDate',
          },
          {
            ...subCategoryProps?.cardsFilter,
            content_type: EntryType.competition,
            'fields.endDate[gte]': getDateOnly(new Date()),
            'fields.hideFromCardGrid[ne]': 'true',
          },
          {
            ...subCategoryProps?.cardsFilter,
            content_type: EntryType.genericPage,
            order: '-fields.publishedDate',
            'fields.heroLogo[exists]': 'true',
          },
        ]}
        numCards={(isDesktop ? 12 : 6) - (metafieldData?.fields?.featuredBanner ? 1 : 0)}
        useLoadingSkeletons
      >
        {children}
      </CardProvider>
    </SubCategoryPropsContext.Provider>
  );
};

export const useSubCategoryProps = (): UseContentProps<SubCategoryProps> | null => {
  const context = useContext(SubCategoryPropsContext);
  if (context === undefined || context === null) {
    throw new Error('useSubCategoryProps must be used within a SubCategoryPropsProvider');
  }

  return context;
};
